import { take, map } from 'rxjs/operators';
import { Subject, ReplaySubject, combineLatest, BehaviorSubject, Observable } from 'rxjs';
import { sha1 } from 'object-hash';
import io from 'socket.io-client';
import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';

class CollectionPaginator {
    constructor(connector, type, options, mfilter) {
        this.connector = connector;
        this.type = type;
        this.options = options;
        this.mfilter = mfilter;
        this._filter = {};
        this.hasNextPage = false;
        this.hasPreviousPage = false;
        this._page = options.page;
        this._offset = options.offset;
        this._range = options.range;
        this._urlExtension = options.urlExtension;
        this._filter = mfilter;
        this._orderOptions = options.orderOptions;
    }
    get filter() {
        return this._filter;
    }
    set filter(value) {
        this._filter = value;
        this.reload();
    }
    get page() {
        return this._page;
    }
    set page(value) {
        this._page = value;
        this.reload();
    }
    get offset() {
        return this._offset;
    }
    set offset(value) {
        this._offset = value;
        this.reload();
    }
    get range() {
        return this._range;
    }
    set range(value) {
        this._range = value;
        this.reload();
    }
    get urlExtension() {
        return this._urlExtension;
    }
    set urlExtension(value) {
        this._urlExtension = value;
        this.reload();
    }
    get orderOptions() {
        return this._orderOptions;
    }
    set orderOptions(value) {
        this._orderOptions = value;
        this.reload();
    }
    updateCount(count) {
        return new Promise((resolve, reject) => {
            this.count = count;
            if (this._range > 0) {
                this.hasPreviousPage = this._page > 1;
                this.hasNextPage = Math.ceil(count / this._range) > this._page;
            }
            resolve();
        });
    }
    reload() {
        this.connector.paginatedLoadCollectionExec(this.type, this._filter, this);
    }
}

/**
 * Data entity unit object
 */
class DataEntity {
    /**
     * Create the data entity
     * @param type Type of the entity
     * @param data Entity data
     * @param connector Reference to the connector
     * @param id Entity id
     * @param embeddingsConf Embeddings configuration
     */
    constructor(type, data, connector = null, id = null, embeddingsConf = null) {
        this.type = type;
        this.connector = connector;
        this.id = id;
        this.embeddingsConf = embeddingsConf;
        /**
         * Entity attributes
         */
        this.attributes = {};
        /**
         * Nested entities
         */
        this.nesting = {};
        // embeddings: {[key: string]: DataEntity[]} = {};
        this.relationship = {};
        /**
         *
         */
        this.embeddings = {};
        for (const key in data) {
            if (data.hasOwnProperty(key) && key !== 'id') {
                this.attributes[key] = data[key];
                if (embeddingsConf && embeddingsConf[key] !== undefined && this.attributes[key]) {
                    if (Array.isArray(this.attributes[key])) {
                        this.embeddings[key] = [];
                        this.attributes[key].forEach((elem) => {
                            this.embeddings[key]
                                .push(new DataEntity(embeddingsConf[key], elem, connector, elem.id, embeddingsConf));
                        });
                    }
                    else {
                        this.embeddings[key] = new DataEntity(embeddingsConf[key], this.attributes[key], connector, this.attributes[key].id, embeddingsConf);
                    }
                }
            }
        }
        if (data.id) {
            this.id = data.id;
        }
        this.generateReferenceObject();
    }
    /**
     * Set an attribute by key
     * @param key Key name
     * @param value New value
     */
    set(key, value) {
        this.attributes[key] = value;
    }
    /**
     * Get an attribute by key
     * @param key Key name
     * @returns Value
     */
    get(key) {
        return this.attributes[key];
    }
    get hasChanges() {
        if (Object.keys(this.attributes).length === 0) {
            return false;
        }
        for (const key in this.attributes) {
            if (this.attributes[key] !== this.attributesRef[key]) {
                return true;
            }
        }
        return false;
    }
    getEmbed(name) {
        return this.embeddings[name];
    }
    /**
     * Save the entity
     * @returns The observable associated to the entity in connector stores
     */
    save(forceReload = false, dispatchBeforeResponse = false) {
        return this.saveAction(forceReload, dispatchBeforeResponse);
    }
    saveAction(forceReload = false, dispatchBeforeResponse = false) {
        let obs;
        if (this.id !== -1) {
            obs = this.connector.saveEntity(this, forceReload, dispatchBeforeResponse);
        }
        else {
            // temporary entity deletion
            this.remove();
            obs = this.connector.createEntity(this.type, this.attributes);
        }
        obs.pipe(take(1)).subscribe(() => {
            this.generateReferenceObject();
        });
        return obs;
    }
    /**
     * Delete the entity
     * @returns True if deletion success
     */
    remove() {
        return this.connector.deleteEntity(this);
    }
    /**
     * Copy the attributes to generate the new reference object (for diff)
     */
    generateReferenceObject() {
        const ref = {};
        const keys = Object.keys(this.attributes);
        keys.forEach((key) => {
            ref[key] = this.attributes[key];
        });
        this.attributesRef = ref;
    }
    /**
     * Get an attributes cloned object
     * @returns The cloned attributes object
     */
    getClone() {
        const clone = {};
        const keys = Object.keys(this.attributes);
        keys.forEach((key) => {
            clone[key] = this.attributes[key];
        });
        return clone;
    }
    /**
     * Return the diff (only updated properties since last save action)
     * @returns Diff object
     */
    getDiff() {
        const diff = {};
        const keys = Object.keys(this.attributes);
        keys.forEach((key) => {
            if (this.attributes[key] !== undefined &&
                this.attributesRef[key] !== this.attributes[key]) {
                diff[key] = this.attributes[key];
            }
        });
        return diff;
    }
}

/**
 * Data collection object
 */
class DataCollection {
    /**
     * Creates the collection
     */
    constructor(type, data, connector = null, structure = null, embeddings = null) {
        this.type = type;
        this.connector = connector;
        this.paginated = false;
        /**
         * Entities contained by the collection
         */
        this.entities = [];
        /**
         * Observables of entities contained by the collection
         */
        this.entitiesObservables = [];
        if (Array.isArray(data)) {
            data.forEach((elem) => {
                if (structure) {
                    elem = structure.filterModel(elem);
                }
                this.entities.push(new DataEntity(type, elem, connector, elem?.id, embeddings));
            });
        }
        else {
            const keys = Object.keys(data);
            keys.forEach((key) => {
                if (structure) {
                    data[key] = structure.filterModel(data[key]);
                }
                this.entities.push(new DataEntity(type, data[key], connector, key, embeddings));
            });
        }
    }
    /**
     * Remove entity from collection
     * @param id Id of the entity to delete
     */
    deleteEntity(id) {
        this.entities.forEach((entity, index) => {
            if (entity.id === id) {
                this.entities.splice(index, 1);
                this.entitiesObservables.splice(index, 1);
            }
        });
    }
    /**
     * Register entity in collection, if not already contained by the collection
     * @param entity Entity to register
     * @param entityObservable Entity observable to register
     */
    registerEntity(entity, entityObservable) {
        let count = 0;
        for (const collectionEntity of this.entities) {
            if (entity.id && entity.id === collectionEntity.id) {
                this.entities[count] = entity;
                this.entitiesObservables[count] = entityObservable;
                return;
            }
            count++;
        }
        this.entities.push(entity);
        this.entitiesObservables.push(entityObservable);
    }
}

class InterfaceError {
    constructor(code = 0, message = '', originalMessage = '', data = {}) {
        this.code = code;
        this.message = message;
        this.originalMessage = originalMessage;
        this.data = data;
    }
}

/**
 * Base external interface
 */
class ExternalInterface {
    constructor() {
        this.unexpectedLogoutSubject = new Subject();
        /**
         * if true, the save method will only send the modified properties to the service
         */
        this.useDiff = false;
    }
    get authenticated() { return undefined; }
    /**
     * Load an entity from the service
     * @param type Name of the endpoint
     * @param id Id of the entity
     * @param errorHandler Function used to handle errors
     * @returns A set of data, or an observable
     */
    loadEntity(type, id, errorHandler = null) {
        console.warn('LoadEntity not implemented in interface');
        return null;
    }
    /**
     * Load an entity collection from the service
     * @param type Name of the endpoint
     * @param filter Collection filter object
     * @param errorHandler Function used to handle errors
     * @returns A collection set of data, or an observable
     */
    loadCollection(type, filter, errorHandler = null) {
        console.warn('LoadCollection not implemented in interface');
        return null;
    }
    /**
     *
     */
    clear() {
        console.warn('LoadCollection not implemented in interface');
    }
    paginatedLoadCollection(type, options, paginator, errorHandler = null) {
        console.warn('PaginatedLoadCollection not implemented in interface');
        return null;
    }
    /**
     * Create an entity on the service
     * @param type Endpoint name
     * @param data Base data used to create the entity
     * @param errorHandler Function used to handle errors
     * @returns A set of data, or an observable
     */
    createEntity(type, data, errorHandler = null) {
        console.warn('CreateEntity not implemented in interface');
        return null;
    }
    /**
     * Delete an entity from the service
     * @param type Name of the endpoint
     * @param id Id of the entity
     * @param errorHandler Function used to handle errors
     * @returns True if deletion success
     */
    deleteEntity(type, id, errorHandler = null) {
        console.warn('DeleteEntity not implemented in interface');
        return null;
    }
    /**
     * Save an entity on the service
     * @param data Data to Save
     * @param type Name of the endpoint
     * @param id Id of the entity
     * @param errorHandler Function used to handle errors
     * @returns The saved data
     */
    saveEntity(data, type, id, errorHandler = null) {
        console.warn('SaveEntity not implemented in interface');
        return null;
    }
    /**
     * Authenticating to the service
     * @param login User login
     * @param password User password
     * @param errorHandler Function used to handle errors
     */
    authenticate(login, password, errorHandler = null) {
        console.warn('Authenticate not implemented in interface');
        return null;
    }
    logout() {
        return null;
    }
    /**
     * Release an endpoint if not useful anymore
     * @param type Name of the endpoint
     */
    release(type) {
        console.warn('Release not implemented in interface');
    }
    /**
     * Sends an error message
     * @param code Error code
     * @param originalMessage Error original text message
     * @param errorHandler Error handler Function
     */
    sendError(code, originalMessage, errorHandler, data = {}) {
        let error = new InterfaceError(code, '', originalMessage, data);
        errorHandler(error);
    }
}

/**
 * Local storage data interface
 */
class LocalStorage extends ExternalInterface {
    /**
     * Create a local storage data interface
     * @param configuration Configuration object
     * @param connector Reference to the connector
     */
    constructor(configuration, connector) {
        super();
        this.configuration = configuration;
        this.connector = connector;
        /**
         * Where are stored the entities
         */
        this.dataStore = {};
        this.useDiff = false;
    }
    /**
     *
     */
    clear() {
        this.dataStore = {};
    }
    /**
     * If a prefix is defined in the configuration, returns a concatenation of prefix and endpoint name
     * @param type Name of the endpoint
     * @returns The prefixed endpoint name
     */
    getPrefixedType(type) {
        if (this.configuration.prefix) {
            return this.configuration.prefix + '-' + type;
        }
        else {
            return type;
        }
    }
    /**
     * Load an endpoint from the local storage
     * @param pointName The endpoint name
     */
    loadPointFromStorage(pointName) {
        if (!localStorage[pointName] || localStorage[pointName] === '') {
            this.dataStore[pointName] = {};
        }
        else {
            this.dataStore[pointName] = JSON.parse(localStorage[pointName]);
        }
    }
    /**
     * Load an endpoint from the local storage if storage object is empty for that name
     * @param type Name of the endpoint
     */
    loadPointFromStorageIfEmpty(type) {
        const pointName = this.getPrefixedType(type);
        if (!this.dataStore[pointName]) {
            this.loadPointFromStorage(pointName);
        }
    }
    /**
     * Set entity data in local store
     * @param type Name of the endpoint
     * @param id Id of the entity
     * @param data Entity Data
     */
    setEntityInStore(type, id, data) {
        const pointName = this.getPrefixedType(type);
        this.loadPointFromStorageIfEmpty(type);
        this.dataStore[pointName][id] = data;
        this.savePointToStorage(type);
    }
    /**
     * Get entity data from local store
     * @param type Name of the endpoint
     * @param id Id of the entity
     * @returns Data of the entity
     */
    getEntityFromStore(type, id) {
        const pointName = this.getPrefixedType(type);
        this.loadPointFromStorageIfEmpty(type);
        return this.dataStore[pointName][id];
    }
    /**
     * Delete entity from local store
     * @param type Name of the endpoint
     * @param id Id of the entity
     * @returns True if deletion success
     */
    deleteEntityFromStore(type, id) {
        const pointName = this.getPrefixedType(type);
        this.loadPointFromStorageIfEmpty(type);
        if (this.dataStore[pointName][id]) {
            delete this.dataStore[pointName][id];
            this.savePointToStorage(type);
            return true;
        }
        return false;
    }
    /**
     * Get collection data from local store
     * @param type Name of the endpoint
     * @param filter Filter data
     * @returns Collection data
     */
    getCollectionFromStore(type, filter = {}) {
        const pointName = this.getPrefixedType(type);
        this.loadPointFromStorageIfEmpty(type);
        const dataSet = {};
        const keys = Object.keys(this.dataStore[pointName]);
        const filterKeys = Object.keys(filter);
        keys.forEach((key) => {
            let matching = true;
            filterKeys.forEach((filterKey) => {
                if (filter[filterKey] !== this.dataStore[pointName][+key][filterKey]) {
                    matching = false;
                }
            });
            if (matching) {
                dataSet[+key] = this.dataStore[pointName][+key];
            }
        });
        return dataSet;
    }
    /**
     * Save local store to localStorage, for a specified endpoint
     * @param type Name of the endpoint
     */
    savePointToStorage(type) {
        const pointName = this.getPrefixedType(type);
        if (this.dataStore[pointName]) {
            localStorage[pointName] = JSON.stringify(this.dataStore[pointName]);
        }
    }
    /**
     * Set last used id to localStorage
     * @param value The value to set to
     */
    set lastUsedId(value) {
        const lastUsedIdKey = this.getPrefixedType('lastusedid');
        localStorage[lastUsedIdKey] = value;
    }
    /**
     * Get last used id from localStorage
     * @returns The value
     */
    get lastUsedId() {
        const lastUsedIdKey = this.getPrefixedType('lastusedid');
        if (localStorage[lastUsedIdKey] === undefined || localStorage[lastUsedIdKey] === '') {
            return 0;
        }
        else {
            return +localStorage[lastUsedIdKey];
        }
    }
    /**
     * Load an entity from local storage service
     * @param type Name of the endpoint
     * @param id Id of the entity
     * @returns The entity raw datas
     */
    loadEntity(type, id) {
        this.loadPointFromStorageIfEmpty(type);
        const data = this.getEntityFromStore(type, id);
        return data ? data : null;
    }
    /**
     * Load a collection from local storage service
     * @param type Name of the endpoint
     * @param filter Filter data
     * @returns The collection raw data
     */
    loadCollection(type, filter = {}) {
        this.loadPointFromStorageIfEmpty(type);
        const data = this.getCollectionFromStore(type, filter);
        return data ? data : null;
    }
    /**
     * Save an entity to the local storage service
     * @param entity Entity data to save
     * @param type Name of the endpoint
     * @param id Id of the entity
     * @returns Saved raw data
     */
    saveEntity(entity, type, id) {
        this.loadPointFromStorageIfEmpty(type);
        this.setEntityInStore(type, id, entity);
        return entity;
    }
    /**
     * Create an entity on the local storage service
     * @param type Name of the endpoint
     * @param data Entity Data
     * @returns The saved raw dats
     */
    createEntity(type, data) {
        const newId = ++this.lastUsedId;
        data.id = newId;
        this.setEntityInStore(type, newId, data);
        return data;
    }
    /**
     * Delete an entity from the local storage service
     * @param type Name of the endpoint
     * @param id Id of the entity
     * @returns True if deletion success
     */
    deleteEntity(type, id) {
        this.loadPointFromStorageIfEmpty(type);
        return this.deleteEntityFromStore(type, id);
    }
}

var OrderDirection;
(function (OrderDirection) {
    OrderDirection["ASC"] = "asc";
    OrderDirection["DESC"] = "desc";
})(OrderDirection || (OrderDirection = {}));

/**
 * Http external interface
 */
class Http extends ExternalInterface {
    /**
     * Creates the http interface
     * @param configuration Configuration object
     * @param connector Reference to the connector
     */
    constructor(configuration, connector, interfaceName) {
        super();
        this.configuration = configuration;
        this.connector = connector;
        this.interfaceName = interfaceName;
        /**
         * Headers sent with each request
         */
        this.headers = {};
        this.useDiff = true;
        this.clear();
        window.addEventListener('storage', (event) => this.OnUnexpectedStorageChange(event));
    }
    /**
     *
     */
    clear() {
        if (this.configuration.headers) {
            for (const header in this.configuration.headers) {
                if (this.configuration.headers.hasOwnProperty(header)) {
                    this.headers[header] = this.configuration.headers[header];
                }
            }
        }
        this.dataStore = {
            user: undefined
        };
    }
    /**
     * Is the user authenticated on this service ?
     */
    get authenticated() {
        const value = new ReplaySubject(1);
        this.dataStore.user = JSON.parse(localStorage.getItem(`${this.interfaceName}_currentUser`));
        const expire = JSON.parse(localStorage.getItem(`${this.interfaceName}_expires_in`));
        if (expire > Date.now()) {
            this.dataStore.user = JSON.parse(localStorage.getItem(`${this.interfaceName}_currentUser`));
            this.setToken(JSON.parse(localStorage.getItem(`${this.interfaceName}_accessToken`))).subscribe((data) => {
                value.next(data);
            }, (err) => {
                value.error(err);
            });
        }
        else if (expire && expire < Date.now()) {
            value.error('Token expired');
            this.logout();
        }
        else {
            value.error('Not authenticated');
        }
        return value;
    }
    /**
     * Add headers to the request
     * @param request A xhr request
     * @param type the endpoint use to find a specific header configuration about the endpoint
     * @param method used to determine if we can do a preflight proof request
     */
    addHeaders(request, type, method) {
        const endPointConf = this.connector.getEndpointConfiguration(type);
        const isAuthFree = !!endPointConf && typeof endPointConf !== 'string' && !!endPointConf.authenticationFree;
        const availableForPreflightProofContentType = ['application/x-www-form-urlencoded', 'multipart/form-data', 'text/plain'];
        const isShouldBePreflightProof = isAuthFree && method === 'GET';
        const isItPreflightProofHeader = (headerValue) => availableForPreflightProofContentType.includes(headerValue);
        for (const headerName in this.headers) {
            if (this.headers.hasOwnProperty(headerName)) {
                if (headerName.toLocaleLowerCase() === 'access-token' && isAuthFree) {
                    // do nothing to avoid access-token header
                }
                else if (headerName.toLocaleLowerCase() === 'content-type'
                    && isShouldBePreflightProof
                    && isItPreflightProofHeader(this.headers[headerName]) === false) {
                    // replace the content-type to a preflight proof content-type
                    request.setRequestHeader('content-type', 'text/plain');
                }
                else {
                    request.setRequestHeader(headerName, this.headers[headerName]);
                }
            }
        }
    }
    apiUrl(endpointName) {
        const useApi = !this.configuration.useApiExtension === false;
        const ext = useApi ? 'api/' : '';
        const endPointConf = this.connector.getEndpointConfiguration(endpointName);
        let useLanguage = false;
        if (endPointConf && typeof endPointConf === 'object') {
            useLanguage = endPointConf.useLanguage;
        }
        if (typeof this.configuration.apiUrl === 'string') {
            if (!useLanguage) {
                return this.configuration.apiUrl + ext;
            }
            else {
                return this.configuration.apiUrl + this.connector.currentLanguage + '/' + ext;
            }
        }
    }
    /**
     * Load entity in http service
     * @param type Endpoint name
     * @param id Id of the entity
     * @param errorHandler Function used to handle errors
     * @returns Observable returning the data
     */
    loadEntity(type, id, errorHandler = null) {
        const request = new XMLHttpRequest();
        const url = `${this.apiUrl(type)}${type}/${id}`;
        request.open('GET', url, true);
        const subject = new ReplaySubject(1);
        this.addHeaders(request, type, 'GET');
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    subject.next(this.extractEntity(request.responseText));
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler, {
                        entityType: type,
                        entityId: id,
                        response: JSON.parse(request.responseText)
                    });
                }
            }
        };
        request.send();
        return subject;
    }
    paginatedLoadCollection(type, options, paginator, errorHandler = null) {
        const request = new XMLHttpRequest();
        let url = `${this.apiUrl(type)}${type}`;
        if (options.urlExtension) {
            if (options.urlExtension.charAt(0) !== '/') {
                url += '/';
            }
            url += options.urlExtension;
        }
        const orderOptionsLength = options.orderOptions ? options.orderOptions.length : 0;
        const filtersLength = options.filter ? Object.keys(options.filter).length : 0;
        if (filtersLength > 0 || orderOptionsLength > 0 || options.offset || options.range || options.page) {
            url += '?';
        }
        let started = false;
        if (orderOptionsLength > 0) {
            started = true;
            url += 'sort=';
            options.orderOptions.forEach((option, index) => {
                url += (option.direction === OrderDirection.DESC ? '-' : '') + option.field;
                if (index < orderOptionsLength - 1) {
                    url += ',';
                }
            });
        }
        if (filtersLength > 0) {
            if (started) {
                url += '&';
            }
            else {
                started = true;
            }
            const keys = Object.keys(options.filter);
            keys.forEach((key, index) => {
                const val = options.filter[key];
                url += `filter[${key}]=${val}`;
                if (index < keys.length - 1) {
                    url += '&';
                }
            });
        }
        if (options.page) {
            if (started) {
                url += '&';
            }
            else {
                started = true;
            }
            url += 'page=' + options.page;
        }
        if (options.range) {
            if (started) {
                url += '&';
            }
            else {
                started = true;
            }
            url += 'range=' + options.range;
        }
        if (options.offset) {
            if (started) {
                url += '&';
            }
            url += 'offset=' + options.offset;
        }
        request.open('GET', url, true);
        this.addHeaders(request, type, 'GET');
        const subject = new ReplaySubject(1);
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    const promise = this.extractCollection(request.responseText, paginator);
                    promise.then((rest) => {
                        subject.next(rest);
                    }).catch((error) => {
                        console.error(error);
                    });
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler, {
                        response: JSON.parse(request.responseText)
                    });
                }
            }
        };
        request.send();
        return subject;
    }
    /**
     * Load a collection in http service
     * @param type Endpoint name
     * @param filter Filter Object
     * @param errorHandler Function used to handle errors
     * @returns Observable returning the collection data
     */
    loadCollection(type, filter = {}, errorHandler = null) {
        const request = new XMLHttpRequest();
        let url = `${this.apiUrl(type)}${type}`;
        const filterKeys = Object.keys(filter);
        if (filterKeys.length === 1 && filterKeys[0] === 'id') {
            url += '/' + filter.id;
        }
        else {
            if (filterKeys.length > 0) {
                url += '?';
            }
            filterKeys.forEach((key, index) => {
                const val = filter[key];
                url += `filter[${key}]=${val}`;
                if (index < filterKeys.length - 1) {
                    url += '&';
                }
            });
        }
        request.open('GET', url, true);
        this.addHeaders(request, type, 'GET');
        const subject = new ReplaySubject(1);
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    const promise = this.extractCollection(request.responseText);
                    promise.then((rest) => {
                        subject.next(rest);
                    }).catch((error) => {
                        console.error(error);
                    });
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler, {
                        response: JSON.parse(request.responseText)
                    });
                }
            }
        };
        request.send();
        return subject;
    }
    /**
     * Save entity to the http service
     * @param entity Entity data to save
     * @param type Endpoint name
     * @param id Id of the entity
     * @param errorHandler Function used to handle errors
     * @returns Observable returning the entity data
     */
    saveEntity(entity, type, id, errorHandler = null) {
        const request = new XMLHttpRequest();
        const url = `${this.apiUrl(type)}${type}/${id}`;
        request.open('PATCH', url, true);
        this.addHeaders(request, type, 'PATCH');
        const subject = new ReplaySubject(1);
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    subject.next(this.extractEntity(request.responseText));
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler, {
                        response: JSON.parse(request.responseText)
                    });
                }
            }
        };
        request.send(JSON.stringify(entity));
        return subject;
    }
    /**
     * Create entity in http service
     * @param type Endpoint name
     * @param data Data used to create the entity
     * @param errorHandler Function used to handle errors
     * @returns Observable returning the entity data
     */
    createEntity(type, data, errorHandler = null) {
        const request = new XMLHttpRequest();
        const url = `${this.apiUrl(type)}${type}`;
        request.open('POST', url, true);
        this.addHeaders(request, type, 'POST');
        const subject = new ReplaySubject(1);
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    subject.next(this.extractEntity(request.responseText));
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler, {
                        response: JSON.parse(request.responseText)
                    });
                }
            }
        };
        request.send(JSON.stringify(data));
        return subject;
    }
    /**
     * Delete entity from http service
     * @param type Endpoint type
     * @param id Entity id
     * @param errorHandler Function used to handle errors
     * @returns True if deletion success
     */
    deleteEntity(type, id, errorHandler = null) {
        const request = new XMLHttpRequest();
        const url = `${this.apiUrl(type)}${type}/${id}`;
        request.open('DELETE', url, true);
        this.addHeaders(request, type, 'DELETE');
        const subject = new ReplaySubject(1);
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    subject.next(true);
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler, {
                        response: JSON.parse(request.responseText)
                    });
                }
            }
        };
        request.send();
        return subject;
    }
    /**
     * Authenticate in service
     * @param login User login
     * @param password User password
     * @param errorHandler Function used to handle errors
     * @returns True if authentication success
     */
    authenticate(login, password, errorHandler = null) {
        const subject = new ReplaySubject(1);
        const request = new XMLHttpRequest();
        const url = `${this.configuration.apiUrl}api/login-token`;
        request.open('GET', url, true);
        request.setRequestHeader('Authorization', 'Basic ' + btoa(login.trim() + ':' + password));
        const observables = [];
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    const loginData = JSON.parse(request.responseText);
                    const expire = +loginData.expires_in - 3600;
                    if (expire < 3600) {
                        if (localStorage.getItem(`${this.interfaceName}_accessToken`)) {
                            observables.push(this.setToken(loginData.access_token, errorHandler));
                            this.setExpireDate(expire);
                            this.setRefreshToken(loginData.refresh_token);
                        }
                        observables.push(this.refreshToken(loginData.refresh_token, errorHandler));
                    }
                    else {
                        observables.push(this.setToken(loginData.access_token, errorHandler));
                        this.setExpireDate(expire);
                        this.setRefreshToken(loginData.refresh_token);
                    }
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler, {
                        response: JSON.parse(request.responseText)
                    });
                }
                combineLatest(...observables).pipe(map((values) => {
                    return values[0];
                })).subscribe((data) => {
                    subject.next(data);
                }, (err) => {
                    subject.error(err);
                });
            }
        };
        request.send();
        return subject;
    }
    logout() {
        // TODO: revoir cette partie du logout
        /*let keys:string[] = Object.keys(this.headers);

        keys.forEach((headerName:string) => {
            if (headerName !== "access-token") {
                delete this.headers[headerName];
            }
        });*/
        localStorage.removeItem(`${this.interfaceName}_currentUser`);
        localStorage.removeItem(`${this.interfaceName}_accessToken`);
        localStorage.removeItem(`${this.interfaceName}_expires_in`);
        localStorage.removeItem(`${this.interfaceName}_refreshToken`);
        if (this.headers.hasOwnProperty('access-token')) {
            delete this.headers['access-token'];
        }
        this.dataStore.user = null;
        return new BehaviorSubject(true);
    }
    setToken(accessToken, errorHandler = null) {
        if (accessToken && accessToken != '') {
            localStorage.setItem(`${this.interfaceName}_accessToken`, JSON.stringify(accessToken));
            this.headers['access-token'] = accessToken;
            return this.getMe(true, errorHandler);
        }
    }
    setExpireDate(expire) {
        const date = Date.now();
        localStorage.setItem(`${this.interfaceName}_expires_in`, JSON.stringify(date + (expire * 1000)));
    }
    refreshToken(refreshToken, errorHandler) {
        const subject = new ReplaySubject(1);
        const request = new XMLHttpRequest();
        const url = `${this.configuration.apiUrl}api/refresh-token/${refreshToken}`;
        request.open('GET', url, true);
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    const userData = JSON.parse(request.responseText);
                    this.setToken(userData.access_token, errorHandler);
                    this.setExpireDate(+userData.expires_in - 3600);
                    this.setRefreshToken(userData.refresh_token);
                    subject.next(userData);
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler, {
                        response: JSON.parse(request.responseText)
                    });
                }
            }
        };
        request.send();
        return subject;
    }
    setRefreshToken(refreshToken) {
        localStorage.setItem(`${this.interfaceName}_refreshToken`, JSON.stringify(refreshToken));
    }
    getMe(complete = true, errorHandler = null) {
        const subject = new ReplaySubject(1);
        const request = new XMLHttpRequest();
        const url = `${this.configuration.apiUrl}api/users/me`;
        request.open('GET', url, true);
        this.addHeaders(request, 'users/me', 'GET');
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    const userData = JSON.parse(request.responseText).data[0];
                    subject.next(userData);
                    this.setMe(userData, complete);
                }
                else if (request.status === 401) {
                    subject.error(request.status);
                }
                else {
                    if (errorHandler) {
                        this.sendError(request.status, request.statusText, errorHandler, {
                            response: JSON.parse(request.responseText)
                        });
                    }
                    subject.error(null);
                }
            }
        };
        request.send();
        return subject;
    }
    setMe(userData, complete = true) {
        if (complete) {
            this.dataStore.user = userData;
            // this.data.next(this.dataStore.user);
            localStorage.setItem(`${this.interfaceName}_currentUser`, JSON.stringify(userData));
        }
        // this.currentUserData = userData;
    }
    /**
     * Extract entity data from raw data
     * @param responseText Response text from server
     * @returns Entity data
     */
    extractEntity(responseText) {
        const data = JSON.parse(responseText);
        // pas sûr que ce code serve
        if (data.data[0] && data.data[0].id !== undefined) {
            data.data[0].id = data.data[0].id;
        }
        if (data.data && data.data.id !== undefined) {
            data.data.id = data.data.id;
        }
        if (data.data[0]) {
            return data.data[0];
        }
        else {
            return data.data;
        }
    }
    /**
     * Extract collection data from raw data
     * @param responseText Response text from server
     * @returns Collection data
     */
    extractCollection(responseText, paginator = null) {
        const data = JSON.parse(responseText);
        const collectionData = {};
        data.data.forEach((entityData) => {
            collectionData['_' + entityData.id] = entityData;
        });
        if (paginator) {
            return paginator.updateCount(+data.count).then(() => {
                return collectionData;
            });
        }
        else {
            return Promise.resolve(collectionData);
        }
    }
    /**
     * Callback appelé lors du déclanchement d'un event de type StorageEvent. Cela n'a lieu que si le localstorage a été modifié depuis une autre page.
     * Dans ce cas, nous controllons s'il s'agit d'une déconnexion pour notifier ensuite les abonnées a OnUnexpectedLogout
     */
    OnUnexpectedStorageChange(event) {
        if (event.key === `${this.interfaceName}_accessToken`
            && event.newValue === null
            && event.oldValue !== null) {
            this.unexpectedLogoutSubject.next();
        }
    }
}

/**
 * Nodejs interface
 */
class Nodejs extends ExternalInterface {
    // private temporaryCollectionsStore:{[key:string]:ReplaySubject<CollectionDataSet>} = {};
    /**
     * Create the nodejs interface
     * @param configuration Interface configuration object
     * @param connector Reference to the connector
     */
    constructor(configuration, connector) {
        super();
        this.configuration = configuration;
        this.connector = connector;
        this.errorsStore = {};
        this.collectionsErrorStore = {};
        this.connected = true;
        this.collectionSubjects = {};
        this.dataCollections = {};
        this.collectionFilters = {};
        this.messagePrefix = this.configuration.messagePrefix || 'message';
        this.retrieveEvent = this.configuration.retrievePrefix || 'retrieve_';
        this.connectionCommand = this.configuration.connectionCommand || 'connexion';
        this.maxRetry = 1000;
        this.initializeSocket();
    }
    clear() {
        this.errorsStore = {};
        this.collectionsErrorStore = {};
        this.collectionSubjects = {};
        this.dataCollections = {};
        this.collectionFilters = {};
    }
    initializeSocket(forced = false) {
        if (this.socket && !forced) {
            return;
        }
        if (this.socket) {
            this.socket.disconnect();
            this.socket.close();
        }
        this.socket = io(this.configuration.socketUrl);
        this.socket.on('connect_failed', function () {
            console.log('Connection Failed');
        });
        this.socket.on('connect_error', (e) => {
            console.log('Connection Error');
        });
        this.socket.on('connect', () => {
            console.log('Connected');
            this.connected = true;
        });
        this.socket.on('reconnecting', () => {
            console.log('reconnecting');
        });
        this.socket.on('disconnect', () => {
            console.log('Disconnected');
            this.connected = false;
            this.dispatchErrors();
        });
        this.socket.on(this.messagePrefix, (data) => {
            if (data.id && data.data) {
                data.data.id = data.id;
            }
            if (data.command === 'put') {
                this.connector.registerEntityByData(data.type, data.id || data.data.id, data.data);
                if (data.type === this.connector.configuration.liveRefreshService) {
                    this.connector.refreshCollectionWithData(data.data.myType, data.data);
                }
            }
            if (data.command === 'update') {
                this.connector.registerEntityByData(data.type, data.id || data.data.id, data.data);
            }
            if (data.command === 'delete') {
                this.connector.unregisterEntityTypeAndId(data.type, data.id);
            }
        });
    }
    /**
     * Send interface errors
     */
    dispatchErrors() {
        // error managing
        for (const id in this.errorsStore) {
            if (this.errorsStore.hasOwnProperty(id)) {
                this.sendError(0, '', this.errorsStore[id]);
                delete this.errorsStore[id];
            }
        }
        for (const id in this.collectionsErrorStore) {
            if (this.collectionsErrorStore.hasOwnProperty(id)) {
                this.sendError(0, '', this.collectionsErrorStore[id]);
                // delete this.collectionsErrorStore[id];
            }
        }
    }
    /**
     * Load entity in nodejs service
     * @param type Endpoint name
     * @param id Id of the entity
     * @param errorHandler Function used to handle errors
     * @returns Observable returning the data
     */
    loadEntity(type, id, errorHandler) {
        if (!this.connected) {
            this.sendError(0, '', errorHandler);
        }
        return this.loadCollection(type, {
            id
        }).pipe(map((data) => {
            return data[0];
        }));
    }
    saveEntity(data, type, id, errorHandler = null) {
        const subject = new ReplaySubject(1);
        const cid = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
        if (!this.connected) {
            this.sendError(0, '', errorHandler);
        }
        else {
            this.errorsStore[cid] = errorHandler;
            const requestData = {
                command: 'update',
                data,
                id,
                type,
                cid
            };
            this.socket.emit('message', requestData);
        }
        return subject;
    }
    /**
     * Load a collection in nodejs service
     * @param type Endpoint name
     * @param filter Filter Object
     * @param errorHandler Function used to handle errors
     * @returns Observable returning the collection data
     */
    loadCollection(type, filter = {}, errorHandler = null) {
        // à voir si il y a besoin d'initialiser
        // this.initializeSocket();
        const hash = sha1(filter);
        const subject = new ReplaySubject(1);
        if (!this.dataCollections[type]) {
            this.dataCollections[type] = {};
        }
        if (!this.collectionFilters[type]) {
            this.collectionFilters[type] = {};
        }
        if (!this.collectionSubjects[type]) {
            this.collectionSubjects[type] = {};
        }
        this.collectionSubjects[type][hash] = subject;
        this.collectionFilters[type][hash] = filter;
        if (!this.connected) {
            this.sendError(0, '', errorHandler);
        }
        else {
            const cid = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
            const evtName = this.retrieveEvent + type;
            this.socket.emit('connexion', type, filter, cid);
            const callback = (data) => {
                const res = {};
                for (const id in data) {
                    const mid = data[id].data.id || data[id].id;
                    res[mid] = data[id].data;
                }
                this.dataCollections[type][hash] = res;
                subject.next(res);
                this.socket.off(evtName, callback);
            };
            this.socket.off(evtName, callback);
            this.collectionsErrorStore[cid] = errorHandler;
            this.socket.on(evtName, callback);
        }
        return subject;
    }
    /**
     * Create entity in nodejs service
     * @param type Endpoint name
     * @param data Data used to create the entity
     * @param errorHandler Function used to handle errors
     * @returns Observable returning the entity data
     */
    createEntity(type, data, errorHandler = null) {
        const subject = new ReplaySubject(1);
        const cid = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
        const id = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
        this.errorsStore[cid] = errorHandler;
        if (!this.connected) {
            this.sendError(0, '', errorHandler);
        }
        else {
            const requestData = {
                command: 'put',
                data,
                type,
                cid,
                id
            };
            this.socket.emit('message', requestData);
        }
        return subject;
    }
    /**
     * Delete entity from nodejs service
     * @param type Endpoint type
     * @param id Entity id
     * @param errorHandler Function used to handle errors
     * @returns True if deletion success
     */
    deleteEntity(type, id, errorHandler = null) {
        const subject = new ReplaySubject(1);
        const cid = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
        this.errorsStore[cid] = errorHandler;
        if (!this.connected) {
            this.sendError(0, '', errorHandler);
        }
        else {
            const requestData = {
                command: 'delete',
                id,
                type,
                cid
            };
            this.socket.emit('message', requestData);
        }
        return subject;
    }
}

/**
 * Collection store: where the collections and the collection observables are stored for an endpoint
 */
class CollectionStore {
    /**
     * Creates the store
     */
    constructor() {
        /**
         * Stored collection observables, indexed by filter hash
         */
        this.collectionObservables = {};
        /**
         * Filters indexed by their hash
         */
        this.filters = {};
        /**
         * Stored collections, indexed by filter hash
         */
        this.collections = {};
    }
    /**
     *
     */
    clearEntities(filter) {
        const hash = sha1(filter);
        if (this.collections[hash]) {
            this.collections[hash].entities.length = 0;
        }
    }
    /**
     * Registers the collection in store and update associated subject. If the subject does not exists, creates it
     * @param collection Collection to register
     * @param filter Collection filter
     * @returns Observable associated to the collection
     */
    registerCollection(collection, filter) {
        const hash = sha1(filter);
        this.filters[hash] = filter;
        this.collections[hash] = collection;
        let collectionSubject;
        if (this.collectionObservables[hash]) {
            collectionSubject = this.collectionObservables[hash];
        }
        else {
            collectionSubject = new Subject();
            this.collectionObservables[hash] = collectionSubject;
        }
        // collectionSubject.next(collection);
        return collectionSubject;
    }
    /**
     * Register entity in collection in store if entity match collection filter
     * @param entity Entity to register
     * @param entityObservable Entity observable to register
     * @param refreshCollection If true, the collection observable is refreshed
     */
    registerEntityInCollections(entity, entityObservable, refreshCollection = true) {
        const collectionKeys = Object.keys(this.collections);
        collectionKeys.forEach((key) => {
            if (this.entityMatchFilter(entity, this.filters[key])) {
                this.collections[key].registerEntity(entity, entityObservable);
                if (refreshCollection) {
                    this.collectionObservables[key].next(this.collections[key]);
                }
            }
        });
    }
    /**
     * Refresh collection observable by filter
     * @param filter Filter object
     */
    refreshCollections(filter) {
        const filterKeys = Object.keys(this.collectionObservables);
        filterKeys.forEach((hash) => {
            if (this.filters[hash] && this.collections[hash] && this.filterMatching(filter, this.filters[hash])) {
                this.collectionObservables[hash].next(this.collections[hash]);
            }
        });
    }
    /**
     * Delete entity from stored collections
     * @param entity Entity to delete
     */
    deleteEntityFromCollection(entity) {
        const collectionKeys = Object.keys(this.collections);
        collectionKeys.forEach((key) => {
            if (this.entityMatchFilter(entity, this.filters[key])) {
                this.collections[key].deleteEntity(entity.id);
                this.collectionObservables[key].next(this.collections[key]);
            }
        });
    }
    /**
     * Delete all data for a specific filter
     * @param filter The filter used to delete data
     */
    unregister(filter) {
        const hash = sha1(filter);
        if (this.collections[hash]) {
            delete this.collections[hash];
        }
        if (this.collectionObservables[hash]) {
            delete this.collectionObservables[hash];
        }
        if (this.filters[hash]) {
            delete this.filters[hash];
        }
    }
    /**
     * Test if the entity matches the filter
     * @param entity Entity to test
     * @param filter Filter object
     * @returns True if the entity matches the filter
     */
    entityMatchFilter(entity, filter) {
        const filterKeys = Object.keys(filter);
        for (const key of filterKeys) {
            if (entity.attributes[key] !== undefined && filter[key] !== entity.attributes[key]) {
                return false;
            }
        }
        return true;
    }
    /**
     *
     * @param filter1
     * @param filter2
     * @returns {boolean}
     */
    filterMatching(filter1 = {}, filter2 = {}) {
        if (!filter1 || !filter2) {
            return false;
        }
        // must have the same keys
        const filter1Keys = Object.keys(filter1);
        const filter2Keys = Object.keys(filter2);
        if (filter1Keys.length !== filter2Keys.length) {
            return false;
        }
        for (const key of filter1Keys) {
            if (filter2Keys.indexOf(key) === -1) {
                return false;
            }
        }
        for (const key of filter1Keys) {
            if (filter1[key] !== filter2[key]) {
                return false;
            }
        }
        return true;
    }
    /**
     * Returns the observable associated to the specified filter
     * @param filter Filter object
     * @returns The observable associated to the filter object
     */
    getCollectionSubject(filter, useCache = false) {
        const hash = sha1(filter);
        if (this.collectionObservables[hash]) {
            return this.collectionObservables[hash];
        }
        else {
            let subject;
            if (useCache) {
                subject = new ReplaySubject();
            }
            else {
                subject = new Subject();
            }
            this.collectionObservables[hash] = subject;
            return subject;
        }
    }
    /**
     * Returns true if collection is defined in store
     * @param filter Collection filter object
     */
    isInStore(filter) {
        const hash = sha1(filter);
        return !!this.collections[hash];
    }
}

/**
 * Entity store: where the entities and the entities observables are stored for an endpoint
 */
class EntityStore {
    /**
     * Creates the store
     */
    constructor() {
        /**
         * Stores entities subjects, indexed by entity id
         */
        this.entitiesObservables = {};
    }
    clear() {
    }
    /**
     * Registers the entity in store and update associated subject. If the subject does not exists, creates it
     * @param entity Entity to register
     * @param id Entity id
     * @returns Observable associated to the entity
     */
    registerEntity(entity, id) {
        if (id !== -1 && this.entitiesObservables[id]) {
            this.entitiesObservables[id].next(entity);
            return this.entitiesObservables[id];
        }
        else {
            const subject = new ReplaySubject(1);
            subject.next(entity);
            if (id !== -1) {
                this.entitiesObservables[id] = subject;
            }
            return subject;
        }
    }
    /**
     * Register an entity subject to a specified id
     * @param id Id used for registration
     * @param subject Subject to register
     */
    registerEntitySubject(id, subject) {
        if (id !== -1) {
            this.entitiesObservables[id] = subject;
        }
    }
    /**
     * Delete all data for a specific entity id
     * @param id Entity id
     */
    unregister(id) {
        if (this.entitiesObservables[id]) {
            delete this.entitiesObservables[id];
        }
    }
    /**
     * Delete entity from store
     * @param entity Entity to delete
     */
    unregisterEntity(entity) {
        delete this.entitiesObservables[entity.id];
    }
    /**
     * Returns the observable associated to the specified id
     * @param id Id used in registration
     * @returns Entity subject associated to the id
     */
    getEntityObservable(id, createObservable = false) {
        if (this.entitiesObservables[id] && createObservable === false) {
            return this.entitiesObservables[id];
        }
        else {
            const subject = new ReplaySubject(1);
            this.entitiesObservables[id] = subject;
            return subject;
        }
    }
    /**
     * Returns true if entity is defined in store
     * @param entityId Entity id
     * @returns {boolean}
     */
    isInStore(entityId) {
        return !!this.entitiesObservables[entityId];
    }
}

/**
 * Http external interface
 */
class Drupal8 extends ExternalInterface {
    /**
     * Creates the http interface
     * @param configuration Configuration object
     * @param connector Reference to the connector
     */
    constructor(configuration, connector, interfaceName) {
        super();
        this.configuration = configuration;
        this.connector = connector;
        this.interfaceName = interfaceName;
        /*
        Headers sent with each request
         */
        this.headers = {};
        this.useDiff = true;
        if (configuration.headers) {
            for (const header in configuration.headers) {
                if (configuration.headers.hasOwnProperty(header)) {
                    this.headers[header] = configuration.headers[header];
                }
            }
        }
        this.dataStore = {
            user: undefined
        };
    }
    /**
     * Is the user authenticated on this service ?
     * @returns {Observable<EntityDataSet<T>>}
     */
    get authenticated() {
        const value = new ReplaySubject(1);
        this.dataStore.user = JSON.parse(localStorage.getItem(`${this.interfaceName}_currentUser`));
        const expire = JSON.parse(localStorage.getItem(`${this.interfaceName}_expires_in`));
        if (expire > Date.now()) {
            this.dataStore.user = JSON.parse(localStorage.getItem(`${this.interfaceName}_currentUser`));
            this.setToken(JSON.parse(localStorage.getItem(`${this.interfaceName}_accessToken`))).subscribe((data) => {
                value.next(data);
            });
        }
        else if (expire && expire < Date.now()) {
            value.error(null);
            this.logOut();
        }
        else {
            value.error(null);
        }
        return value;
    }
    /**
     * Add headers to the request
     * @param request A xhr request
     */
    addHeaders(request) {
        for (const headerName in this.headers) {
            request.setRequestHeader(headerName, this.headers[headerName]);
        }
    }
    /**
     * Load entity in http service
     * @param type Endpoint name
     * @param id Id of the entity
     * @param errorHandler Function used to handle errors
     * @returns Observable returning the data
     */
    loadEntity(type, id, errorHandler = null) {
        const request = new XMLHttpRequest();
        const url = `${this.configuration.apiUrl}${type}/${id}`;
        request.open('GET', url, true);
        const subject = new ReplaySubject(1);
        this.addHeaders(request);
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    subject.next(this.extractEntity(request.responseText));
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler);
                }
            }
        };
        request.send();
        return subject;
    }
    /**
     * Load a collection in http service
     * @param type Endpoint name
     * @param filter Filter Object
     * @param errorHandler Function used to handle errors
     * @returns Observable returning the collection data
     */
    loadCollection(type, filter = {}, errorHandler = null) {
        const request = new XMLHttpRequest();
        let url = `${this.configuration.apiUrl}${type}`;
        const filterKeys = Object.keys(filter);
        if (filterKeys.length > 0) {
            url += '?';
        }
        filterKeys.forEach((key, index) => {
            const val = filter[key];
            url += `filter[${key}]=${val}`;
            if (index < filterKeys.length - 1) {
                url += '&';
            }
        });
        request.open('GET', url, true);
        this.addHeaders(request);
        const subject = new ReplaySubject(1);
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    subject.next(this.extractCollection(request.responseText));
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler);
                }
            }
        };
        request.send();
        return subject;
    }
    /**
     * Save entity to the http service
     * @param entity Entity data to save
     * @param type Endpoint name
     * @param id Id of the entity
     * @param errorHandler Function used to handle errors
     * @returns Observable returning the entity data
     */
    saveEntity(entity, type, id, errorHandler = null) {
        const request = new XMLHttpRequest();
        const url = `${this.configuration.apiUrl}${type}/${id}`;
        request.open('PATCH', url, true);
        this.addHeaders(request);
        const subject = new ReplaySubject(1);
        const saveObject = {
            data: {
                id,
                type,
                attributes: entity
            }
        };
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    subject.next(this.extractEntity(request.responseText));
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler);
                }
            }
        };
        request.send(JSON.stringify(saveObject));
        return subject;
    }
    /**
     * Create entity in http service
     * @param type Endpoint name
     * @param data Data used to create the entity
     * @param errorHandler Function used to handle errors
     * @returns Observable returning the entity data
     */
    createEntity(type, data, errorHandler = null) {
        const request = new XMLHttpRequest();
        const url = `${this.configuration.apiUrl}${type}`;
        request.open('POST', url, true);
        this.addHeaders(request);
        const addObject = {
            data: {
                attributes: data,
                type
            }
        };
        const subject = new ReplaySubject(1);
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 201) {
                    subject.next(this.extractEntity(request.responseText));
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler);
                }
            }
        };
        request.send(JSON.stringify(addObject));
        return subject;
    }
    /**
     * Delete entity from http service
     * @param type Endpoint type
     * @param id Entity id
     * @param errorHandler Function used to handle errors
     * @returns True if deletion success
     */
    deleteEntity(type, id, errorHandler = null) {
        const request = new XMLHttpRequest();
        const url = `${this.configuration.apiUrl}${type}/${id}`;
        request.open('DELETE', url, true);
        this.addHeaders(request);
        const subject = new ReplaySubject(1);
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 204) {
                    subject.next(true);
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler);
                }
            }
        };
        request.send();
        return subject;
    }
    getOauthToken(login, password) {
        const subject = new ReplaySubject(1);
        const req = new XMLHttpRequest();
        const url = `${this.configuration.apiUrl}oauth/token`;
        req.open('POST', url, true);
        req.onreadystatechange = () => {
            if (req.readyState === XMLHttpRequest.DONE) {
                if (req.status === 200) {
                    subject.next(JSON.parse(req.responseText));
                }
                else {
                    subject.error(req.responseText);
                }
            }
        };
        const data = {
            grant_type: 'password',
            client_id: this.configuration.clientId,
            userName: login,
            password,
            scope: this.configuration.scope || 'administrator angular'
        };
        if (this.configuration.clientSecret) {
            data.client_secret = this.configuration.clientSecret;
        }
        req.send(data);
        return subject;
    }
    /**
     * Authenticate in service
     * @param login User login
     * @param password User password
     * @param errorHandler Function used to handle errors
     * @returns True if authentication success
     */
    authenticate(login, password, errorHandler = null) {
        const subject = new ReplaySubject(1);
        const request = new XMLHttpRequest();
        const url = `${this.configuration.apiUrl}oauth/token`;
        request.open('POST', url, true);
        request.setRequestHeader('content-type', 'application/x-www-form-urlencoded');
        // request.setRequestHeader("Authorization", 'Basic ' + btoa(login.trim() + ':' + password));
        const observables = [];
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    localStorage[`${this.interfaceName}_userName`] = login;
                    const loginData = JSON.parse(request.responseText);
                    const expire = +loginData.expires_in - 3600;
                    if (expire < 3600) {
                        if (localStorage.getItem(`${this.interfaceName}_accessToken`)) {
                            observables.push(this.setToken(loginData.access_token, errorHandler));
                            this.setExpireDate(expire);
                            this.setRefreshToken(loginData.refresh_token);
                        }
                        observables.push(this.refreshToken(loginData.refresh_token, errorHandler));
                    }
                    else {
                        observables.push(this.setToken(loginData.access_token, errorHandler));
                        this.setExpireDate(expire);
                        this.setRefreshToken(loginData.refresh_token);
                    }
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler);
                }
                combineLatest(...observables).pipe(map((values) => {
                    return values[0];
                })).subscribe((data) => {
                    subject.next(data);
                });
            }
        };
        const data = {
            grant_type: 'password',
            client_id: this.configuration.clientId,
            username: login,
            password,
            scope: this.configuration.scope || 'administrator angular',
            client_secret: undefined
        };
        if (this.configuration.clientSecret) {
            data.client_secret = this.configuration.clientSecret;
        }
        else {
            delete data.client_secret;
        }
        let dataStr = '';
        for (const id in data) {
            dataStr += id + '=' + data[id] + '&';
        }
        request.send(dataStr);
        return subject;
    }
    logOut() {
        // TODO: revoir cette partie du logout
        /*let keys:string[] = Object.keys(this.headers);

        keys.forEach((headerName:string) => {
            if (headerName !== "access-token") {
                delete this.headers[headerName];
            }
        });*/
        localStorage.removeItem(`${this.interfaceName}_currentUser`);
        localStorage.removeItem(`${this.interfaceName}_accessToken`);
        localStorage.removeItem(`${this.interfaceName}_expires_in`);
        localStorage.removeItem(`${this.interfaceName}_refreshToken`);
        this.dataStore.user = null;
    }
    /**
     *
     * @param accessToken
     * @param errorHandler
     */
    setToken(accessToken, errorHandler = null) {
        if (accessToken && accessToken != '') {
            localStorage.setItem(`${this.interfaceName}_accessToken`, JSON.stringify(accessToken));
            this.headers.Authorization = 'Bearer ' + accessToken;
            return this.getMe(true, errorHandler);
        }
    }
    /**
     *
     * @param expire
     */
    setExpireDate(expire) {
        const date = Date.now();
        localStorage.setItem(`${this.interfaceName}_expires_in`, JSON.stringify(date + (expire * 1000)));
    }
    /**
     *
     * @param refreshToken
     * @param errorHandler
     */
    refreshToken(refreshToken, errorHandler) {
        const subject = new ReplaySubject(1);
        const request = new XMLHttpRequest();
        const url = `${this.configuration.apiUrl}refresh-token/${refreshToken}`;
        request.open('GET', url, true);
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    const userData = JSON.parse(request.responseText);
                    this.setToken(userData.access_token, errorHandler);
                    this.setExpireDate(+userData.expires_in - 3600);
                    this.setRefreshToken(userData.refresh_token);
                    subject.next(userData);
                }
                else {
                    this.sendError(request.status, request.statusText, errorHandler);
                }
            }
        };
        request.send();
        return subject;
    }
    /**
     *
     * @param refreshToken
     */
    setRefreshToken(refreshToken) {
        localStorage.setItem(`${this.interfaceName}_refreshToken`, JSON.stringify(refreshToken));
    }
    /**
     *
     * @param complete
     * @param errorHandler
     * @returns {Observable<EntityDataSet<T>>}
     */
    getMe(complete = true, errorHandler = null) {
        const subject = new ReplaySubject(1);
        const request = new XMLHttpRequest();
        const url = `${this.configuration.apiUrl}user?filter[name][value]=${localStorage[`${this.interfaceName}_userName`]}`;
        request.open('GET', url, true);
        this.addHeaders(request);
        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    const userData = JSON.parse(request.responseText).data[0].attributes;
                    userData.id = userData.uuid;
                    subject.next(userData);
                    this.setMe(userData, complete);
                }
                else {
                    if (errorHandler) {
                        this.sendError(request.status, request.statusText, errorHandler);
                    }
                    subject.error(null);
                }
            }
        };
        request.send();
        return subject;
    }
    /**
     *
     * @param userData
     * @param complete
     */
    setMe(userData, complete = true) {
        if (complete) {
            this.dataStore.user = userData;
            // this.data.next(this.dataStore.user);
            localStorage.setItem(`${this.interfaceName}_currentUser`, JSON.stringify(userData));
        }
        // this.currentUserData = userData;
    }
    /**
     * Extract entity data from raw data
     * @param responseText Response text from server
     * @returns Entity data
     */
    extractEntity(responseText) {
        const data = JSON.parse(responseText).data.attributes;
        data.id = data.uuid;
        return data;
    }
    /**
     * Extract collection data from raw data
     * @param responseText Response text from server
     * @returns Collection data
     */
    extractCollection(responseText) {
        const data = JSON.parse(responseText);
        const collectionData = {};
        data.data.forEach((entityData) => {
            entityData.attributes.id = entityData.attributes.uuid;
            collectionData[entityData.attributes.uuid] = entityData.attributes;
        });
        return collectionData;
    }
}

class CordovaLocal extends ExternalInterface {
    constructor(configuration, connector, interfaceName) {
        super();
        this.configuration = configuration;
        this.connector = connector;
        this.interfaceName = interfaceName;
        this.dataStore = {};
        // this.loadPointFromStorage("ideaswall");
    }
    createFile(dirEntry, fileName, text, isAppend, success, fail) {
        dirEntry.getFile(fileName, { create: true, exclusive: false }, fileEntry => {
            this.writeFile(fileEntry, text, e => {
                success(e);
            }, e => fail(e));
        }, e => {
            fail(e);
        });
    }
    writeFile(fileEntry, dataObj, success, fail) {
        fileEntry.createWriter(function (fileWriter) {
            fileWriter.onwriteend = function () {
                success();
            };
            fileWriter.onerror = function (e) {
                fail(e);
            };
            fileWriter.write(dataObj);
        });
    }
    readFile(fileEntry, success, fail) {
        fileEntry.file(function (file) {
            const reader = new FileReader();
            reader.onloadend = function () {
                success(this.result);
            };
            reader.readAsText(file);
        }, e => {
            fail(e);
        });
    }
    createFileInPersistentStorage(fileName, textContent, success, fail) {
        this.createFileInSpace('persistent', fileName, textContent, success, fail);
    }
    createFileInSpace(space, fileName, textContent, success, fail) {
        window.requestFileSystem(LocalFileSystem.PERSISTENT, 0, () => {
            window.resolveLocalFileSystemURL('cdvfile://localhost/' + space + '/', dirEntry => {
                this.createFile(dirEntry, fileName, textContent, false, e => {
                    success(e);
                }, e => fail(e));
            }, e => {
                fail(e);
            });
        }, e => {
            fail(e);
        });
    }
    readFileInPersistentStorage(fileName, success, fail) {
        this.readFileInSpace('persistent', fileName, success, fail);
    }
    readFileInAppDirectory(fileName, success, fail) {
        let space;
        if (device.platform === 'Android') {
            space = 'assets';
        }
        else if (device.platform === 'iOS') {
            space = 'bundle';
        }
        this.readFileInSpace(space, fileName, success, fail);
    }
    authenticate() {
        const obs = new ReplaySubject(1);
        /*obs.next({
            "id":"5",
            "label":"christophe",
            "role":[2,3],
            "email":"christophe.vimbert@tralalere.com",
            "you_are":null,
            "type":null,
            "contact_email":null,
            "find_us":null,
            "email_status":null,
            "newsletter":false,
            "picture":"https:\/\/preprod.lms.tralalere.com\/assets\/images\/avatars\/profile.jpg",
            "access":"1543576103",
            "groups":["234","270","271"]
        });*/
        return obs;
    }
    get authenticated() {
        const obs = new ReplaySubject(1);
        /*obs.next({
            "id":"5",
            "label":"christophe",
            "role":[2,3],
            "email":"christophe.vimbert@tralalere.com",
            "you_are":null,
            "type":null,
            "contact_email":null,
            "find_us":null,
            "email_status":null,
            "newsletter":false,
            "picture":"https:\/\/preprod.lms.tralalere.com\/assets\/images\/avatars\/profile.jpg",
            "access":"1543576103",
            "groups":["234","270","271"]
        });*/
        return obs;
    }
    readEndpoint(endpointName, success, fail) {
        // si le fichier n'existe pas dans le dossier de stockage, on copie celui qui se trouve dans le dossier de l'appli
        // sinon on lit celui-ci
        this.readFileInPersistentStorage(endpointName + '.json', text => {
            success(text);
            console.log('Fichier lu depuis persistant');
        }, e => {
            // le fichier doit être copié dans le stockage persistant
            this.readFileInAppDirectory('www/assets/endpoints/' + endpointName + '.json', oText => {
                /*const cdata: Object = JSON.parse(oText);
                let newObj: Object = {};

                cdata["data"].forEach(elem => {
                    newObj[elem["id"]] = elem;
                });

                oText = JSON.stringify({
                    data: newObj
                });*/
                this.createFileInPersistentStorage(endpointName + '.json', oText, () => {
                    success(oText);
                    console.log('Fichier copié dans persistant');
                }, () => {
                    console.log('erreur de copie dans le dossier');
                });
            }, e => {
                console.log('Erreur bizarre', e);
            });
        });
    }
    saveToEndPoint(endpointName, content, success, fail) {
        this.createFileInPersistentStorage(endpointName + '.json', content, () => {
            success();
        }, e => {
            fail(e);
        });
    }
    readFileInSpace(space, fileName, success, fail) {
        window.requestFileSystem(LocalFileSystem.PERSISTENT, 0, () => {
            window.resolveLocalFileSystemURL('cdvfile://localhost/' + space + '/' + fileName, fileEntry => {
                this.readFile(fileEntry, text => success(text), e => fail(e));
            }, e => {
                fail(e);
            });
        }, e => {
            fail(e);
        });
    }
    getCollectionFromStore(type, filter = {}, callback) {
        const pointName = type;
        this.loadPointFromStorageIfEmpty(type, () => {
            const dataSet = {};
            let obj = {};
            /*if (Array.isArray(this.dataStore[pointName])) {
                this.dataStore[pointName].forEach(elem => {
                    obj[elem["id"]] = elem;
                });*/
            // } else {
            obj = this.dataStore[pointName];
            // }
            const keys = Object.keys(obj);
            const filterKeys = Object.keys(filter);
            keys.forEach((key) => {
                let matching = true;
                filterKeys.forEach((filterKey) => {
                    if (filter[filterKey] !== obj[+key][filterKey]) {
                        matching = false;
                    }
                });
                if (matching) {
                    dataSet[+key] = obj[+key];
                }
            });
            callback(dataSet);
        });
    }
    getEntityFromStore(type, id, callback) {
        const pointName = type;
        this.loadPointFromStorageIfEmpty(type, () => {
            callback(this.dataStore[pointName][String(id)]);
        });
    }
    deleteEntityFromStore(type, id, callback) {
        const pointName = type;
        this.loadPointFromStorageIfEmpty(type, () => {
            if (this.dataStore[pointName][String(id)]) {
                delete this.dataStore[pointName][String(id)];
                this.savePointToStorage(type);
                return true;
            }
            callback(false);
        });
    }
    // TODO
    set lastUsedId(value) {
        const lastUsedIdKey = 'lastusedid';
        localStorage[lastUsedIdKey] = value;
    }
    /**
     * Get last used id from localStorage
     * @returns The value
     */
    // TODO
    get lastUsedId() {
        const lastUsedIdKey = 'lastusedid';
        if (localStorage[lastUsedIdKey] === undefined || localStorage[lastUsedIdKey] === '') {
            return 0;
        }
        else {
            return +localStorage[lastUsedIdKey];
        }
    }
    savePointToStorage(type) {
        const pointName = type;
        console.log('77', this.dataStore[pointName]);
        if (this.dataStore[pointName]) {
            const tobj = {};
            for (const key in this.dataStore[pointName]) {
                if (this.dataStore[pointName].hasOwnProperty(key)) {
                    tobj[key] = this.dataStore[pointName][key];
                }
            }
            const obj = {
                data: tobj
            };
            const str = JSON.stringify(obj);
            console.log('save point ts', str, obj);
            this.saveToEndPoint(type, str, () => {
                console.log('Succès de l\'enregistrement du endpoint');
            }, () => {
                console.log('Echec de l\'enregistrement du endpoint');
            });
        }
    }
    loadPointFromStorage(pointName, success) {
        console.log('3');
        this.readEndpoint(pointName, text => {
            console.log(JSON.parse(text));
            this.dataStore[pointName] = JSON.parse(text).data;
            success();
        }, () => {
            this.dataStore[pointName] = {};
        });
    }
    loadPointFromStorageIfEmpty(type, success) {
        const pointName = type;
        console.log('2');
        if (!this.dataStore[pointName]) {
            this.loadPointFromStorage(pointName, () => {
                success();
            });
        }
        else {
            success();
        }
    }
    loadEntity(type, id) {
        const subject = new ReplaySubject(1);
        this.loadPointFromStorageIfEmpty(type, () => {
            this.getEntityFromStore(type, id, (data) => {
                subject.next(data ? data : null);
            });
        });
        return subject;
    }
    loadCollection(type, filter = {}) {
        const subject = new ReplaySubject(1);
        this.loadPointFromStorageIfEmpty(type, () => {
            this.getCollectionFromStore(type, filter, (data) => {
                subject.next(data ? data : null);
            });
        });
        return subject;
    }
    saveEntity(entity, type, id) {
        const subject = new ReplaySubject(1);
        this.loadPointFromStorageIfEmpty(type, () => {
            this.setEntityInStore(type, id, entity);
            subject.next(entity);
        });
        return subject.asObservable();
    }
    createEntity(type, data) {
        const subject = new ReplaySubject(1);
        const newId = ++this.lastUsedId;
        data.id = newId;
        this.setEntityInStore(type, newId, data);
        subject.next(data);
        return subject.asObservable();
    }
    deleteEntity(type, id) {
        const subject = new ReplaySubject(1);
        this.loadPointFromStorageIfEmpty(type, () => {
            this.deleteEntityFromStore(type, id, (val) => {
                subject.next(val);
            });
        });
        return subject.asObservable();
    }
    setEntityInStore(type, id, data) {
        const pointName = type;
        this.loadPointFromStorageIfEmpty(type, () => {
            if (!this.dataStore[pointName]) {
                this.dataStore[pointName] = {};
            }
            this.dataStore[pointName][String(id)] = data;
            this.savePointToStorage(type);
        });
    }
}

/**
 * Data connector class
 */
class DataConnector {
    /**
     * Create a dataConnector
     * @param configuration Data connector configuration
     */
    constructor(configuration) {
        this.configuration = configuration;
        /**
         * Available interfaces
         * @type {{}} External interfaces, indexed by name
         */
        this.interfaces = {};
        /**
         * Entities store
         * @type {{}} Entities stores, indexed by endpoint name
         */
        this.entitiesLiveStore = {};
        /**
         * Collections store
         * @type {{}} Collections stores, indexed by endpoint name
         */
        this.collectionsLiveStore = {};
        /**
         * Server push listeners
         * @type {{}} Listeners, indexed by endpoint name
         */
        this.pushListeners = {};
        /**
         * Built-in external interfaces
         */
        this.builtInFactories = {
            localstorage: LocalStorage,
            http: Http,
            nodejs: Nodejs,
            drupal8: Drupal8,
            cordova: CordovaLocal
        };
        this.globalMessageSubject = new ReplaySubject(1);
        if (this.configuration.declarations) {
            for (const declarationKey in this.configuration.declarations) {
                if (this.configuration.declarations.hasOwnProperty(declarationKey)) {
                    this.builtInFactories[declarationKey] = this.builtInFactories[this.configuration.declarations[declarationKey]];
                }
            }
        }
        for (const interfaceName in configuration.configuration) {
            if (configuration.configuration.hasOwnProperty(interfaceName)) {
                this.interfaces[interfaceName] = new this.builtInFactories[interfaceName](configuration.configuration[interfaceName], this, interfaceName);
            }
        }
        this.retryTimeout = this.configuration.retryTimeout || 50000;
        this.maxRetry = this.configuration.maxRetry || 5;
        if (typeof this.configuration.language === 'string') {
            this.currentLanguage = this.configuration.language;
        }
        else if (this.configuration.language instanceof Observable) {
            this.configuration.language.subscribe((language) => {
                this.currentLanguage = language;
            });
        }
    }
    sendMessage(error = null) {
        this.globalMessageSubject.next(error);
    }
    getRetryTimeout(type) {
        return this.retryTimeout;
    }
    getMaxRetry(type) {
        return this.retryTimeout;
    }
    setLanguage(language) {
        if (typeof this.configuration.language === 'string' || !this.configuration.language) {
            this.currentLanguage = language;
        }
    }
    globalCallback(code) {
        const callbackId = '';
        this.configuration.globalCallback(callbackId);
    }
    /**
     * Get data interface by endpoint name
     * @param type Endpoint name
     * @returns External interface
     */
    getInterface(type) {
        const conf = this.getEndpointConfiguration(type);
        if (typeof conf === 'string') {
            return this.interfaces[conf];
        }
        else if (conf && conf.type) {
            return this.interfaces[conf.type];
        }
        else {
            return this.interfaces[this.configuration.defaultInterface];
        }
    }
    /**
     * Get endpoint configuration
     * @param type Endpoint name
     * @returns Type of the endpoint, or endpoint configuration object
     */
    getEndpointConfiguration(type) {
        return this.configuration.map[type];
    }
    /**
     * Get model schema used by the endpoint
     * @param type Endpoint name
     * @returns The model schema
     */
    getEndpointStructureModel(type) {
        const conf = this.getEndpointConfiguration(type);
        if (conf && typeof conf === 'object') {
            return conf.structure;
        }
    }
    /**
     * Get nesting attributes types
     * @param type Endpoint name
     * @returns The nested attributes types
     */
    getNesting(type) {
        const conf = this.getEndpointConfiguration(type);
        if (conf && typeof conf === 'object') {
            return conf.nesting || null;
        }
    }
    getDatas(type) {
        const conf = this.getEndpointConfiguration(type);
        if (conf && typeof conf === 'object') {
            return conf.datas || null;
        }
    }
    getEmbeddings(type) {
        const conf = this.getEndpointConfiguration(type);
        if (conf && typeof conf === 'object') {
            return conf.embeddings || null;
        }
    }
    /**
     * Is this endpoint using connector cache
     * @param type Name of the endpoint
     * @returns True if the endpoint use cache
     */
    useCache(type) {
        const conf = this.getEndpointConfiguration(type);
        if (conf && typeof conf === 'object') {
            return !!conf.cached;
        }
        return false;
    }
    /**
     * Get optional keys excluded for saving entities in this endpoint
     * @param type Endpoint name
     * @returns A list of string keys
     */
    getExclusions(type) {
        const conf = this.getEndpointConfiguration(type);
        if (conf && typeof conf === 'object') {
            return conf.exclusions ? conf.exclusions : [];
        }
        return [];
    }
    /**
     * Get the observable associated to an entity from the store
     * @param type Endpoint name
     * @param id Id of the entity
     * @returns The observable associated to the entity
     */
    getEntityObservableInStore(type, id) {
        if (this.entitiesLiveStore[type]) {
            return this.entitiesLiveStore[type].getEntityObservable(id);
        }
        return null;
    }
    /**
     * Get the observable associated to the collection from the store
     * @param type Endpoint name
     * @param filter Filter object
     * @param useCache Store the result in cache or retrieve it from cache
     * @param createIfNotExisted Create the observable if not existed
     * @returns The observable associated to the collection
     */
    getCollectionObservableInStore(type, filter, useCache = false, createIfNotExisted = true) {
        const isExisted = this.collectionsLiveStore[type] && this.collectionsLiveStore[type].isInStore(filter);
        if (isExisted || createIfNotExisted) {
            return this.collectionsLiveStore[type].getCollectionSubject(filter, useCache);
        }
        return null;
    }
    /**
     * Get the observable associated to an entity from the store, if the store is undefined, create it
     * @param type Endpoint name
     * @param id Id of the entity
     * @param createObservable
     * @returns The observable associated to the entity
     */
    getEntitySubject(type, id, createObservable = false) {
        if (!this.entitiesLiveStore[type]) {
            this.entitiesLiveStore[type] = new EntityStore();
        }
        return this.entitiesLiveStore[type].getEntityObservable(id, createObservable);
    }
    /**
     * Register entity in the stores
     * @param type Endpoint name
     * @param id Id of the entity
     * @param entity Entity
     * @param entityObservable Observable to register
     * @returns The observable associated to the entity
     */
    registerEntity(type, id, entity, entityObservable) {
        if (!this.entitiesLiveStore[type]) {
            this.entitiesLiveStore[type] = new EntityStore();
        }
        if (!this.collectionsLiveStore[type]) {
            this.collectionsLiveStore[type] = new CollectionStore();
        }
        this.collectionsLiveStore[entity.type].registerEntityInCollections(entity, entityObservable);
        return this.entitiesLiveStore[type].registerEntity(entity, id);
    }
    registerEntityByData(type, id, entityData) {
        if (!this.entitiesLiveStore[type]) {
            this.entitiesLiveStore[type] = new EntityStore();
        }
        if (!this.collectionsLiveStore[type]) {
            this.collectionsLiveStore[type] = new CollectionStore();
        }
        const entity = new DataEntity(type, entityData, this, id);
        const obs = this.getEntitySubject(type, id);
        this.collectionsLiveStore[entity.type].registerEntityInCollections(entity, obs);
        this.entitiesLiveStore[type].registerEntity(entity, id);
        if (this.pushListeners[type]) {
            this.pushListeners[type].next(entity);
        }
    }
    registerCollectionEntities(type, collection) {
        if (!this.entitiesLiveStore[type]) {
            this.entitiesLiveStore[type] = new EntityStore();
        }
        if (!this.collectionsLiveStore[type]) {
            this.collectionsLiveStore[type] = new CollectionStore();
        }
        const entitiesObservables = [];
        collection.entities.forEach((entity) => {
            const entityObservable = this.getEntitySubject(type, entity.id);
            this.collectionsLiveStore[entity.type].registerEntityInCollections(entity, entityObservable, false);
            entitiesObservables.push(this.entitiesLiveStore[type].registerEntity(entity, entity.id));
        });
        return entitiesObservables;
    }
    replaceCollectionEntities(type, collection, filter) {
        if (!this.entitiesLiveStore[type]) {
            this.entitiesLiveStore[type] = new EntityStore();
        }
        if (!this.collectionsLiveStore[type]) {
            this.collectionsLiveStore[type] = new CollectionStore();
        }
        this.collectionsLiveStore[type].clearEntities(filter);
        const entitiesObservables = [];
        collection.entities.forEach((entity) => {
            const entityObservable = this.getEntitySubject(type, entity.id);
            this.collectionsLiveStore[entity.type].registerEntityInCollections(entity, entityObservable, false);
            entitiesObservables.push(this.entitiesLiveStore[type].registerEntity(entity, entity.id));
        });
        return entitiesObservables;
    }
    /**
     * Associate an entity suject the the entity in the entity store
     * @param type Endpoint name
     * @param id Id of the entity
     * @param subject Subject to associate
     */
    registerEntitySubject(type, id, subject) {
        if (!this.entitiesLiveStore[type]) {
            this.entitiesLiveStore[type] = new EntityStore();
        }
        this.entitiesLiveStore[type].registerEntitySubject(id, subject);
    }
    /**
     * Get observable associated to the collection from the store. If store is undefined, create it
     * @param type Endpoint name
     * @param filter Filter object
     * @param useCache
     * @returns Observable associated to the collection
     */
    getCollectionObservable(type, filter, useCache = false) {
        if (!this.collectionsLiveStore[type]) {
            this.collectionsLiveStore[type] = new CollectionStore();
        }
        return this.collectionsLiveStore[type].getCollectionSubject(filter, useCache);
    }
    /**
     * Register the collection and collection entities in the store
     * @param type Endpoint name
     * @param filter Filter object
     * @param collection Collection to register
     * @param refresh
     * @returns The observable associated to the collection
     */
    registerCollection(type, filter, collection, refresh = true) {
        if (!this.collectionsLiveStore[type]) {
            this.collectionsLiveStore[type] = new CollectionStore();
        }
        if (!collection.paginated) {
            collection.entitiesObservables = this.registerCollectionEntities(type, collection);
        }
        else {
            collection.entitiesObservables = this.replaceCollectionEntities(type, collection, filter);
        }
        const obs = this.collectionsLiveStore[type].registerCollection(collection, filter);
        // refresh de la collection
        if (refresh) {
            this.collectionsLiveStore[type].refreshCollections(filter);
        }
        return obs;
    }
    paginatedRegisterCollection(type, filter, collection, refresh = true) {
        if (!this.collectionsLiveStore[type]) {
            this.collectionsLiveStore[type] = new CollectionStore();
        }
        collection.entitiesObservables = this.registerCollectionEntities(type, collection);
        const obs = this.collectionsLiveStore[type].registerCollection(collection, filter);
        // refresh de la collection
        if (refresh) {
            this.collectionsLiveStore[type].refreshCollections(filter);
        }
        return obs;
    }
    /**
     * Authenticate to the service
     * @param serviceName Name of service on which we authenticate
     * @param login User login
     * @param password User password
     */
    authenticate(serviceName, login, password) {
        const selectedInterface = this.interfaces[serviceName];
        const subject = new ReplaySubject(1);
        const errorHandler = (error) => {
            this.sendMessage(error);
            subject.error(error);
        };
        selectedInterface.authenticate(login, password, errorHandler).pipe(map((data) => {
            this.sendMessage();
            return new DataEntity('users', data, this, data.id);
        })).subscribe((entity) => {
            subject.next(entity);
        });
        return subject;
    }
    authenticated(serviceName) {
        const selectedInterface = this.interfaces[serviceName];
        return selectedInterface.authenticated.pipe(map((data) => {
            return new DataEntity('users', data, this, data.id);
        }));
    }
    logout(serviceName) {
        const selectedInterface = this.interfaces[serviceName];
        this.clear();
        return selectedInterface.logout();
    }
    /**
     * Release endpoint if not used
     * @param type Endpoint name
     */
    release(type) {
    }
    clear() {
        this.entitiesLiveStore = {};
        this.collectionsLiveStore = {};
        this.pushListeners = {};
        for (const key in this.interfaces) {
            this.interfaces[key].clear();
        }
    }
    /**
     * Listen for an endpoint to be notified when data is pushed from the backend
     * @param type Endpoint name
     * @returns DataEntity observable associated to this entity
     */
    listen(type) {
        if (!this.pushListeners[type]) {
            this.pushListeners[type] = new Subject();
        }
        return this.pushListeners[type];
    }
    /**
     * Load entity in specified endpoint
     * @param type Endpoint name
     * @param id Entity id
     * @returns DataEntity observable associated to this entity
     */
    loadEntity(type, id) {
        if (this.useCache(type)) {
            const obs = this.getEntityObservableInStore(type, id);
            if (obs) {
                return obs;
            }
        }
        const selectedInterface = this.getInterface(type);
        let count = 0;
        let entityData;
        if (selectedInterface) {
            const entitySubject = this.getEntitySubject(type, id, true);
            const structure = this.getEndpointStructureModel(type);
            const embeddings = this.getEmbeddings(type);
            const checkResponse = () => {
                this.sendMessage();
                if (entityData instanceof Observable) {
                    entityData.subscribe((entity) => {
                        if (entity) {
                            if (structure) {
                                entity = structure.filterModel(entity);
                            }
                            // hasNesting ?
                            // let nested:{[key:string]:string} = this.getNesting(type);
                            this.registerEntity(type, id, new DataEntity(type, entity, this, id, embeddings), entitySubject);
                        }
                    });
                }
                else {
                    if (entityData) {
                        if (structure) {
                            entityData = structure.filterModel(entityData);
                        }
                        const newEntity = new DataEntity(type, entityData, this, id, embeddings);
                        // hasNesting ?
                        /*let nested:{[key:string]:string} = this.getNesting(type);

                        if (nested) {
                            let nestedKeys:string[] = Object.keys(nested);

                            nestedKeys.forEach((key:string) => {
                                let nestedObservables:Observable<DataEntity>[] = [];

                                if (entityData[key] !== undefined && typeof entityData[key] === "number") {
                                    this.loadEntity(nested[key], entityData[key]).subscribe((loadedEntity:DataEntity) => {
                                        newEntity.nesting[key] = loadedEntity;
                                    });
                                }

                                if (entityData[key] !== undefined && Array.isArray(entityData[key])) {
                                    this.loadEntities(nested[key], entityData[key]).subscribe((loadedEntities:DataEntity[]) => {
                                        newEntity.nesting[key] = loadedEntities;
                                    });
                                }
                            });
                        }*/
                        this.registerEntity(type, id, newEntity, entitySubject);
                    }
                }
            };
            const errorHandler = (error) => {
                const msg = `Error loading entity of type '${type}' with id ${id}. Error ${error.code}`;
                console.warn(msg);
                error.message = msg;
                this.sendMessage(error);
                if (error.code > 0) {
                    entitySubject.error(error);
                    this.entitiesLiveStore[type].unregister(id);
                }
                else {
                    if (count < this.getMaxRetry(type) || this.getMaxRetry(type) === -1) {
                        setTimeout(() => {
                            entityData = selectedInterface.loadEntity(type, id, errorHandler);
                            checkResponse();
                        }, this.getRetryTimeout(type));
                        count++;
                    }
                    else {
                        entitySubject.error(error);
                        this.entitiesLiveStore[type].unregister(id);
                    }
                }
            };
            entityData = selectedInterface.loadEntity(type, id, errorHandler);
            checkResponse();
            return entitySubject;
        }
    }
    /**
     * Load many entities
     * @param type Endpoint name
     * @param ids Entities ids array
     * @returns The data entities
     */
    loadEntities(type, ids) {
        // TODO: check si une méthode loadEntities optimisée existe dans l'interface
        // TODO: Pourrait retourner un objet indexé par id plutôt qu'un Array
        const observables = [];
        ids.forEach((id) => {
            observables.push(this.loadEntity(type, id));
        });
        return combineLatest(...observables);
    }
    paginatedLoadCollection(type, options) {
        const paginator = new CollectionPaginator(this, type, options, options.filter);
        return this.paginatedLoadCollectionExec(type, options.filter || {}, paginator);
    }
    paginatedLoadCollectionExec(type, filter, paginator) {
        const selectedInterface = this.getInterface(type);
        const structure = this.getEndpointStructureModel(type);
        let count = 0;
        const embeddings = this.getEmbeddings(type);
        if (selectedInterface) {
            const collectionSubject = this.getCollectionObservable(type, filter);
            let collection;
            const checkResponse = () => {
                this.sendMessage();
                if (collection instanceof Observable) {
                    collection.subscribe((newCollection) => {
                        // ici ?
                        const coll = new DataCollection(type, newCollection, this, structure, embeddings);
                        coll.paginated = true;
                        this.registerCollection(type, filter, coll);
                    });
                }
                else {
                    // et là ??
                    const coll = new DataCollection(type, collection, this, structure, embeddings);
                    coll.paginated = true;
                    this.registerCollection(type, filter, coll);
                }
            };
            const errorHandler = (error) => {
                const msg = `Error loading collection of type '${type}' with data ${JSON.stringify(filter)}`;
                console.warn(msg);
                error.message = msg;
                this.sendMessage(error);
                if (error.code > 0) {
                    collectionSubject.error(error);
                    this.collectionsLiveStore[type].unregister(filter);
                }
                else {
                    if (count < this.getMaxRetry(type) || this.getMaxRetry(type) === -1) {
                        setTimeout(() => {
                            collection = selectedInterface.paginatedLoadCollection(type, {
                                filter,
                                page: paginator.page,
                                range: paginator.range,
                                offset: paginator.offset,
                                urlExtension: paginator.urlExtension,
                                orderOptions: paginator.orderOptions
                            }, paginator, errorHandler);
                            checkResponse();
                        }, this.getRetryTimeout(type));
                        count++;
                    }
                    else {
                        collectionSubject.error(error);
                        this.collectionsLiveStore[type].unregister(filter);
                    }
                }
            };
            collection = selectedInterface.paginatedLoadCollection(type, {
                filter,
                page: paginator.page,
                range: paginator.range,
                offset: paginator.offset,
                urlExtension: paginator.urlExtension,
                orderOptions: paginator.orderOptions
            }, paginator, errorHandler);
            checkResponse();
            return {
                collectionObservable: collectionSubject,
                paginator
            };
        }
        return null;
    }
    /**
     * Load collection from specified endpoint
     * @param type Endpoint name
     * @param filter Filter object
     * @returns Observable associated to this collection
     */
    loadCollection(type, filter = {}) {
        const useCache = this.useCache(type);
        if (useCache) {
            const obs = this.getCollectionObservableInStore(type, filter, useCache);
            if (obs) {
                return obs;
            }
        }
        const selectedInterface = this.getInterface(type);
        const structure = this.getEndpointStructureModel(type);
        let count = 0;
        const embeddings = this.getEmbeddings(type);
        if (selectedInterface) {
            const collectionSubject = this.getCollectionObservable(type, filter, useCache);
            let collection;
            const checkResponse = () => {
                this.sendMessage();
                if (collection instanceof Observable) {
                    // attention, dans le cas de nodeJs, on ne doit pas faire de take(1)
                    collection.subscribe((newCollection) => {
                        this.registerCollection(type, filter, new DataCollection(type, newCollection, this, structure, embeddings));
                    });
                }
                else {
                    this.registerCollection(type, filter, new DataCollection(type, collection, this, structure, embeddings));
                }
            };
            const errorHandler = (error) => {
                const msg = `Error loading collection of type '${type}' with data ${JSON.stringify(filter)}`;
                console.warn(msg);
                error.message = msg;
                this.sendMessage(error);
                if (error.code > 0) {
                    collectionSubject.error(error);
                    this.collectionsLiveStore[type].unregister(filter);
                }
                else {
                    if (count < this.getMaxRetry(type) || this.getMaxRetry(type) === -1) {
                        setTimeout(() => {
                            collection = selectedInterface.loadCollection(type, filter, errorHandler);
                            checkResponse();
                        }, this.getRetryTimeout(type));
                        count++;
                    }
                    else {
                        collectionSubject.error(error);
                        this.collectionsLiveStore[type].unregister(filter);
                    }
                }
            };
            collection = selectedInterface.loadCollection(type, filter, errorHandler);
            checkResponse();
            return collectionSubject;
        }
        return null;
    }
    sendReloadNotification(type, data = null) {
        const conf = this.getEndpointConfiguration(type);
        // il faut supprimer les embeddings de l'objet, pour le pas surcharger le service nodejs
        if (conf && typeof conf === 'object') {
            if (conf.refreshEnabled) {
                const dataToSend = {};
                const embeddings = this.getEmbeddings(type);
                for (const key in data) {
                    if (!embeddings || embeddings[key] === undefined) {
                        dataToSend[key] = data[key];
                    }
                }
                const refreshData = {
                    myType: type
                };
                if (data) {
                    refreshData.data = dataToSend;
                }
                this.createEntity(this.configuration.liveRefreshService, refreshData, false);
            }
        }
    }
    /**
     * Save entity
     * @param entity Entity to save
     * @param forceReload whether to reload data if nothing is saved or not
     * @param dispatchBeforeResponse register entity before save happens
     * @returns Observable associated to the entity
     */
    saveEntity(entity, forceReload = false, dispatchBeforeResponse = false) {
        const selectedInterface = this.getInterface(entity.type);
        const structure = this.getEndpointStructureModel(entity.type);
        let dataToSave;
        if (selectedInterface.useDiff) {
            dataToSave = entity.getDiff();
        }
        else {
            dataToSave = entity.getClone();
        }
        const exclusions = this.getExclusions(entity.type);
        exclusions.forEach((key) => {
            if (dataToSave[key]) {
                delete dataToSave[key];
            }
        });
        // let entitySubject:ReplaySubject<DataEntity> = this.getEntitySubject(entity.type, entity.id);
        const entitySubject = new ReplaySubject(1);
        let count = 0;
        let entityData;
        const embeddings = this.getEmbeddings(entity.type);
        if (dispatchBeforeResponse) {
            this.registerEntity(entity.type, entity.id, entity, entitySubject);
        }
        const checkResponse = () => {
            this.sendMessage();
            if (entityData instanceof Observable) {
                entityData.subscribe((saveEntity) => {
                    if (structure) {
                        saveEntity = structure.filterModel(saveEntity);
                    }
                    const ent = new DataEntity(entity.type, saveEntity, this, entity.id, embeddings);
                    entitySubject.next(ent);
                    this.registerEntity(entity.type, entity.id, ent, entitySubject);
                    this.sendReloadNotification(entity.type, saveEntity);
                });
            }
            else {
                if (structure) {
                    entityData = structure.filterModel(entityData);
                }
                const ent = new DataEntity(entity.type, entityData, this, entity.id, embeddings);
                entitySubject.next(ent);
                this.registerEntity(entity.type, entity.id, ent, entitySubject);
                this.sendReloadNotification(entity.type, entityData);
            }
        };
        const errorHandler = (error) => {
            const msg = `Error saving entity of type '${entity.type}' with id ${entity.id}`;
            console.warn(msg);
            error.message = msg;
            this.sendMessage(error);
            if (error.code > 0) {
                entitySubject.error(error);
                this.entitiesLiveStore[entity.type].unregister(entity.id);
            }
            else {
                if (count < this.getMaxRetry(entity.type) || this.getMaxRetry(entity.type) === -1) {
                    setTimeout(() => {
                        if (!selectedInterface.useDiff || Object.keys(dataToSave).length > 0) {
                            entityData = selectedInterface.saveEntity(dataToSave, entity.type, entity.id, errorHandler);
                            checkResponse();
                        }
                        else {
                        }
                    }, this.getRetryTimeout(entity.type));
                    count++;
                }
                else {
                    entitySubject.error(error);
                    this.entitiesLiveStore[entity.type].unregister(entity.id);
                }
            }
        };
        if (!selectedInterface.useDiff || Object.keys(dataToSave).length > 0) {
            entityData = selectedInterface.saveEntity(dataToSave, entity.type, entity.id, errorHandler);
            checkResponse();
        }
        else if (forceReload) {
            this.loadEntity(entity.type, entity.id).pipe(take(1))
                .subscribe((newEntity) => {
                entitySubject.next(newEntity);
            });
        }
        else {
            entitySubject.next(entity);
        }
        return entitySubject;
    }
    /**
     * Create entity to the specified endpoint service
     * @param type Endpoint name
     * @param data Data used to create the entity
     * @returns The observable associated to this entity
     */
    createEntity(type, data = {}, sendNotification = true) {
        const selectedInterface = this.getInterface(type);
        const structure = this.getEndpointStructureModel(type);
        if (structure) {
            data = structure.generateModel(null, data);
        }
        const exclusions = this.getExclusions(type);
        exclusions.forEach((key) => {
            if (data[key]) {
                delete data[key];
            }
        });
        const entitySubject = new ReplaySubject(1);
        let count = 0;
        let entity;
        const embeddings = this.getEmbeddings(type);
        const checkResponse = () => {
            this.sendMessage();
            if (entity instanceof Observable) {
                entity.subscribe((createdEntity) => {
                    this.registerEntitySubject(type, createdEntity.id, entitySubject);
                    this.registerEntity(type, createdEntity.id, new DataEntity(type, createdEntity, this, createdEntity.id, embeddings), entitySubject);
                    if (sendNotification) {
                        this.sendReloadNotification(type, createdEntity);
                    }
                });
            }
            else {
                this.registerEntitySubject(type, entity.id, entitySubject);
                this.registerEntity(type, entity.id, new DataEntity(type, entity, this, entity.id, embeddings), entitySubject);
                if (sendNotification) {
                    this.sendReloadNotification(type, entity);
                }
            }
        };
        const errorHandler = (error) => {
            const msg = `Error creating entity of type '${type}'`;
            console.warn(msg);
            error.message = msg;
            this.sendMessage(error);
            if (error.code > 0) {
                entitySubject.error(error);
            }
            else {
                if (count < this.getMaxRetry(type) || this.getMaxRetry(type) === -1) {
                    setTimeout(() => {
                        entity = selectedInterface.createEntity(type, data, errorHandler);
                        checkResponse();
                    }, this.getRetryTimeout(type));
                    count++;
                }
                else {
                    entitySubject.error(error);
                }
            }
        };
        entity = selectedInterface.createEntity(type, data, errorHandler);
        checkResponse();
        return entitySubject;
    }
    /**
     * Creates an entity on the front only (will be saved on the server later)
     * @param type Endpoint type
     * @param data Data used to create the entity
     * @returns The observable associated to this entity
     */
    createTemporaryEntity(type, data = {}) {
        const structure = this.getEndpointStructureModel(type);
        if (structure) {
            data = structure.generateModel(null, data);
        }
        const entitySubject = new ReplaySubject(1);
        const entity = new DataEntity(type, data, this, -1);
        // pas utile
        // entitySubject.next(entity);
        // attention, pas d'id, car pas de retour du serveur
        this.registerEntitySubject(type, null, entitySubject);
        this.registerEntity(type, null, entity, entitySubject);
        return entitySubject;
    }
    /**
     * Delete an entity
     * @param entity Entity to delete
     * @returns True if deletion success
     */
    deleteEntity(entity) {
        const selectedInterface = this.getInterface(entity.type);
        const subject = new ReplaySubject(1);
        let count = 0;
        const checkResponse = () => {
            this.sendMessage();
            const cloned = Object.assign({}, entity.attributes);
            cloned.id = entity.id;
            if (result instanceof Observable) {
                result.subscribe((res) => {
                    this.unregisterEntity(entity);
                    subject.next(res);
                    this.sendReloadNotification(entity.type, entity.attributes);
                });
            }
            else {
                this.unregisterEntity(entity);
                subject.next(result);
                this.sendReloadNotification(entity.type, entity.attributes);
            }
        };
        let result;
        const errorHandler = (error) => {
            const msg = `Error deleting entity of type '${entity.type}' with id ${entity.id}`;
            console.warn(msg);
            error.message = msg;
            this.sendMessage(error);
            if (error.code > 0) {
                subject.error(error);
                this.entitiesLiveStore[entity.type].unregister(entity.id);
            }
            else {
                if (count < this.getMaxRetry(entity.type) || this.getMaxRetry(entity.type) === -1) {
                    setTimeout(() => {
                        result = selectedInterface.deleteEntity(entity.type, entity.id, errorHandler);
                        checkResponse();
                    }, this.getRetryTimeout(entity.type));
                    count++;
                }
                else {
                    subject.error(error);
                    this.entitiesLiveStore[entity.type].unregister(entity.id);
                }
            }
        };
        result = selectedInterface.deleteEntity(entity.type, entity.id, errorHandler);
        checkResponse();
        return subject;
    }
    /**
     * Delete entity from store
     * @param entity Entity to delete
     */
    unregisterEntity(entity) {
        if (this.collectionsLiveStore[entity.type]) {
            this.collectionsLiveStore[entity.type].deleteEntityFromCollection(entity);
        }
        if (this.entitiesLiveStore[entity.type]) {
            this.entitiesLiveStore[entity.type].unregisterEntity(entity);
        }
    }
    unregisterEntityTypeAndId(type, id) {
        this.entitiesLiveStore[type].getEntityObservable(id).pipe(take(1)).subscribe((entity) => {
            this.unregisterEntity(entity);
        });
    }
    /**
     * Refresh entity (from refresh service)
     * @param type Endpoint name
     * @param id Entity id
     */
    refreshEntity(type, id) {
        const selectedInterface = this.getInterface(type);
        if (this.entitiesLiveStore[type] && this.entitiesLiveStore[type].isInStore(id)) {
            this.loadEntity(type, id);
        }
    }
    /**
     * Refresh collection (from refresh service)
     * @param type Endpoint name
     * @param filter Collection filter object
     */
    refreshCollection(type, filter) {
        // let selectedInterface:ExternalInterface = this.getInterface<T>(type);
        if (this.collectionsLiveStore[type] && this.collectionsLiveStore[type].isInStore(filter)) {
            this.loadCollection(type, filter);
        }
    }
    objectMatchFilter(object, filter) {
        const filterKeys = Object.keys(filter);
        for (const key of filterKeys) {
            if (object[key] !== undefined && filter[key] !== object[key]) {
                return false;
            }
        }
        return true;
    }
    refreshCollectionWithData(type, data) {
        const store = this.collectionsLiveStore[type];
        const conf = this.getEndpointConfiguration(type);
        if (conf && typeof conf === 'object' && store) {
            if (conf.refreshEnabled) {
                for (const key in store.collections) {
                    const filter = store.filters[key];
                    if (this.objectMatchFilter(data, filter)) {
                        this.loadCollection(type, filter);
                    }
                }
            }
        }
    }
    refreshAllCollectionsOfType(type) {
        const store = this.collectionsLiveStore[type];
        const conf = this.getEndpointConfiguration(type);
        if (conf && typeof conf === 'object' && store) {
            if (conf.refreshEnabled) {
                for (const key in store.collections) {
                    const filter = store.filters[key];
                    this.loadCollection(type, filter);
                }
            }
        }
    }
    getUnexpectedLogoutSubject(serviceName) {
        const selectedInterface = this.interfaces[serviceName];
        return selectedInterface.unexpectedLogoutSubject;
    }
}

class ConfigurationProvider {
    constructor(configuration) {
        this.configuration = configuration;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ConfigurationProvider, deps: [{ token: 'configuration' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ConfigurationProvider }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ConfigurationProvider, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['configuration']
                }] }] });

class OctopusConnectService extends DataConnector {
    constructor(configurationProvider) {
        super(configurationProvider.configuration);
        this.configurationProvider = configurationProvider;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OctopusConnectService, deps: [{ token: ConfigurationProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OctopusConnectService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OctopusConnectService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: ConfigurationProvider }] });

class OctopusConnectModule {
    static forRoot(configuration) {
        return {
            ngModule: OctopusConnectModule,
            providers: [
                ConfigurationProvider,
                { provide: 'configuration', useValue: configuration }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OctopusConnectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: OctopusConnectModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OctopusConnectModule, providers: [
            OctopusConnectService
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OctopusConnectModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [
                        OctopusConnectService
                    ]
                }]
        }] });

/*
 * Public API Surface of octopus-connect
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CollectionPaginator, DataCollection, DataConnector, DataEntity, ExternalInterface, InterfaceError, OctopusConnectModule, OctopusConnectService, OrderDirection };
